import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { BusinessUnitViewModel, EsraPipingAPIClient } from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

@Component({
    selector: 'app-business-unit-add-edit',
    templateUrl: './business-unit-add-edit.component.html',
    styleUrls: ['./business-unit-add-edit.component.scss'],
    standalone: false
})
export class BusinessUnitAddEditComponent implements OnInit {
  inputFormGroup: UntypedFormGroup;
  businessUnitToUpdate: BusinessUnitViewModel | null = null;

  title: string = '';
  genericMissingValueMessage: string = 'This is a required field';

  isEditMode: boolean = false;
  isCreateMode: boolean = false;
  submitClicked: boolean = false;
  saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly dialogRef: MatDialogRef<BusinessUnitAddEditComponent, boolean>,
    private readonly esraApiClient: EsraPipingAPIClient,
    private readonly snackBarService: SnackBarService,
    private readonly formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) private readonly businessUnit: BusinessUnitViewModel | null
  ) {
    this.inputFormGroup = this.formBuilder.group({
      name: ['', Validators.required],
      alwaysAvailableForEveryUser: [false]
    });
  }

  ngOnInit(): void {
    this.businessUnitToUpdate = this.businessUnit ? new BusinessUnitViewModel(this.businessUnit) : null;
    this.isEditMode = this.businessUnitToUpdate !== null;
    this.isCreateMode = !this.isEditMode;

    if (this.isCreateMode) {
      this.title = 'Create a new business unit';
    }

    if (this.businessUnitToUpdate) {
      this.title = `Edit ${this.businessUnitToUpdate.name}`;
    }

    if (this.businessUnitToUpdate) {
      this.setFormValues();
    }
  }

  setFormValues(): void {
    if (!this.businessUnitToUpdate) {
      return;
    }

    this.inputFormGroup.setValue({
      name: this.businessUnitToUpdate.name || '',
      alwaysAvailableForEveryUser: this.businessUnitToUpdate.alwaysAvailableForEveryUser || false
    });
  }

  save(): void {
    this.submitClicked = true;

    if (this.inputFormGroup.invalid) {
      this.inputFormGroup.markAllAsTouched();
      return;
    }

    const payload: BusinessUnitViewModel = new BusinessUnitViewModel({
      name: this.inputFormGroup.controls['name'].value,
      isActive: true,
      alwaysAvailableForEveryUser: this.inputFormGroup.controls['alwaysAvailableForEveryUser'].value
    });

    if (this.isCreateMode) {
      this.esraApiClient.createBusinessUnit(payload).pipe(
        indicate(this.saving$),
        first()
      ).subscribe({
        next: () => {
          this.snackBarService.showSnackBar(true, `The new business unit, ${payload.name}, was successfully created.`);
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.snackBarService.showSnackBar(true, err.response, 'mat-snack-bar-error');
        }
      });
    }

    const businessUnitToUpdate: BusinessUnitViewModel | null = this.businessUnitToUpdate;
    if (this.isEditMode && businessUnitToUpdate) {
      businessUnitToUpdate.name = payload.name;
      businessUnitToUpdate.alwaysAvailableForEveryUser = payload.alwaysAvailableForEveryUser;
      this.esraApiClient.updateBusinessUnit(businessUnitToUpdate).pipe(
        indicate(this.saving$),
        first()
      ).subscribe({
        next: () => {
          this.snackBarService.showSnackBar(true, `Business unit was updated to ${businessUnitToUpdate.name}`);
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.snackBarService.showSnackBar(true, err.response, 'mat-snack-bar-error');
        }
      });
    }
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }
}
